/**
 * The house treatment for a `DateRangePicker` — shortcut presets, the brand-purple range
 * calendar, the dropdown paper, and the field chrome — in ONE place.
 *
 * ## Why this is a module and not a component
 *
 * `DateRangePickerFilter` is a DRAWER filter: it takes a DataGrid `column`, renders a
 * `FilterField` label + Clear row around the picker, and emits `{field, operator, value}` pairs
 * into the `Filters` staging array. None of that is reusable by a picker that is a TOP-LEVEL
 * page control (the analytics header's date range, which scopes every number on the page and
 * writes straight to redux) — so wrapping it was never an option.
 *
 * The alternative was to copy the styling to the second call site, which is exactly the second
 * hand-roll working-conventions §36 says to extract instead: this styling is ~120 lines of
 * class-selector overrides that took several passes to get right (the range band's inner corners,
 * the `today` endpoint's stray 45px round, the 49px input height, the `input`-not-root fill), and
 * a copy of it would start drifting on the first fix applied to one and not the other.
 *
 * So the two call sites share the CHROME and each keeps its own BEHAVIOUR. Anything that is a
 * decision about what the picker *does* — the staged value, the Apply/Clear action bar, the
 * `layout` padding that reserves room for that action bar — deliberately stays at the call site.
 *
 * Everything below is `DateRangePickerFilter`'s existing treatment moved verbatim; the drawer
 * filter's rendered appearance is unchanged by the extraction.
 */

import React from 'react'

import dayjs, { Dayjs } from 'dayjs'

import { IconButton, Typography, styled, type Theme } from '@mui/material'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import {
  DateRangePickerDay as MuiDateRangePickerDay,
  type DateRangePickerDayProps,
  type DateRange,
  type PickersRangeCalendarHeaderProps,
  type PickersShortcutsItem,
} from '@mui/x-date-pickers-pro'

import { FIELD_RADIUS, fieldAccentColor, fieldBorderColor, fieldFill, navSelected } from '@repo/ui'

import { FILTER_DROPDOWN_RADIUS } from './dropdownSurface'

/**
 * ── DISPLAY FORMAT ────────────────────────────────────────────────────────────────
 *
 * The written-month shape both range fields render, e.g. `Aug 3, 2026 – Aug 27, 2026`,
 * replacing the numeric `08/03/2026 – 08/27/2026`.
 *
 * NO ORDINAL SUFFIX ("Aug 3rd 2026") — considered and rejected, recorded here so it is a
 * decision rather than a silent deviation. The control shows TWO dates side by side, so the
 * suffix is paid for twice; it adds width to a field that is already the widest control in
 * the row, and it aids nothing: "3" and "3rd" are equally scannable, and the ordinal is a
 * pure ornament once the month name is already spelled out. `utils/date.ts`'s
 * `ABSOLUTE_DATETIME_FORMAT` does use ordinals, but that is a single timestamp in a table
 * cell, not a pair in a fixed-width field.
 *
 * WHY THESE ARE dayjs TOKEN STRINGS AND NOT `utils/date()` (§28 — reuse was checked first).
 * `utils/date.ts` exposes `date()` / `datetime()`, but those are **date-fns functions that
 * take a value and return a string**. A picker field is not a display: it is an editable
 * masked input, and MUI builds its editable SECTIONS from a **dayjs token string**. There is
 * no way to hand it a formatter function, and the two libraries do not even share tokens
 * (date-fns `d`/`yyyy` vs dayjs `D`/`YYYY`). So the reuse is impossible in kind, not merely
 * inconvenient — hence a documented constant here, in the module that already owns this
 * picker's shared chrome, rather than a third copy at each call site.
 */
export const DATE_RANGE_FIELD_FORMAT = 'MMM D, YYYY'

/** The same shape with a clock time — `Aug 3, 2026 3:13 PM`. See `isWholeDayRange`. */
export const DATE_TIME_RANGE_FIELD_FORMAT = 'MMM D, YYYY h:mm A'

const atDayStart = (d: Dayjs) => d.isSame(d.startOf('day'))
const atDayEnd = (d: Dayjs) => d.isSame(d.endOf('day'))

/**
 * True when the range sits exactly on day boundaries — which is the test for whether the
 * field should show a TIME at all.
 *
 * The full both-ends form (`Aug 3, 2026 3:13 PM – Aug 27, 2026 11:59 PM`) is ~44 characters,
 * and the common case does not need it: a brand looking at "last 30 days" is asking a
 * question about days. So time is shown only once the range stops being expressible in days,
 * i.e. once the user has actually set one.
 *
 * `to` accepts EITHER end-of-day or start-of-day. End-of-day is what the presets below and
 * the calendar produce; start-of-day is what a bare `dayjs('2026-08-27')` produces, and a
 * range ending at midnight is still a whole-day statement, not a timed one.
 *
 * An empty or invalid range returns `true` — there is no time to show, so the compact form
 * is right by default.
 */
export const isWholeDayRange = (value: DateRange<Dayjs>): boolean => {
  const [from, to] = value
  if (!from?.isValid() || !to?.isValid()) return true
  return atDayStart(from) && (atDayEnd(to) || atDayStart(to))
}

/**
 * The shortcut presets listed down the left of the calendar popup.
 *
 * EVERY PRESET EXCEPT "Last 24 hours" IS DAY-BOUNDARIED, and that is load-bearing rather
 * than cosmetic. These used to end at `dayjs()` — the current wall-clock instant — so
 * "This month" meant "the 1st at 00:00 through today at 2:41 PM". Two consequences:
 *
 *  1. It was a ragged window. A date filter of `onOrBefore: today 2:41 PM` silently drops
 *     rows created at 3 PM today, which is the kind of quiet under-count nobody reports.
 *  2. It made the range un-expressible in days, so `isWholeDayRange` above would report
 *     every preset as timed and the field would render the 44-character form for all of
 *     them — exactly the case the compact form exists for.
 *
 * "Last 24 hours" keeps its clock times deliberately: it IS a sub-day window, and showing
 * `3:13 PM` for it is the honest reading, not clutter.
 *
 * ── LABEL/ARITHMETIC AUDIT (2026-08-27) ───────────────────────────────────────────
 *
 * Every preset below was re-checked against its own label after the "Last year" bug
 * (`subtract(6, 'year')` under a one-year label). That class of defect — a multiplier that
 * disagrees with the words — is SILENT: the range is valid, the chart renders, and nothing
 * says it answered a different question. So each one is stated here rather than trusted:
 *
 *   Last 24 hours  −24h → now                                   ✓ 24
 *   Last week      previous CALENDAR week, Sun–Sat              ✓ 1
 *   This month     1st of this month → end of today             ✓
 *   Last month     −1 month → end of today (ROLLING)            ✓ 1
 *   Last 3 months  −3 months → end of today                     ✓ 3
 *   Last 6 months  −6 months → end of today                     ✓ 6
 *   This year      Jan 1 → end of today                         ✓
 *   Last year      −1 year → end of today                       ✓ 1  (was 6)
 *
 * No sibling carries the same defect. There IS one remaining inconsistency, left alone
 * deliberately: "Last week" is the previous CALENDAR week while "Last month" is a ROLLING
 * month, so the two adjacent presets read the same but mean different things. Fixing it
 * changes what `DateRangePickerFilter` returns on every table in the platform, which is a
 * behaviour change well outside this pass — recorded, not silently altered.
 */
/**
 * The preset labels, named once.
 *
 * A label here is not decoration — it is the KEY a consumer selects a preset by.
 * `matchDateRangeShortcut` returns one, and the ROI hero's period control picks its three
 * options out of `makeDateRangeShortcutItems`'s array by label so that its windows are
 * BY CONSTRUCTION the same windows the page's date picker offers. That is the whole point
 * of selecting rather than redefining: two definitions of "last 3 months" on one page,
 * one scoping the hero and one scoping everything else, would look identical and diverge
 * the first time either is adjusted.
 *
 * Constants rather than repeated literals so that a rename cannot silently drop an option
 * from the hero (a lookup miss offers nothing, which is a quiet regression) — with one
 * source, a rename either compiles everywhere or nowhere.
 */
export const DATE_RANGE_SHORTCUTS = {
  last24Hours: 'Last 24 hours',
  lastWeek: 'Last week',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  last3Months: 'Last 3 months',
  last6Months: 'Last 6 months',
  thisYear: 'This year',
  lastYear: 'Last year',
  billingPeriod: 'This billing period',
  allTime: 'All time',
} as const

export const dateRangeShortcutItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: DATE_RANGE_SHORTCUTS.last24Hours,
    getValue: () => {
      const today = dayjs()
      return [today.subtract(24, 'hours'), today]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.lastWeek,
    getValue: () => {
      const today = dayjs()
      const prevWeek = today.subtract(7, 'day')
      return [prevWeek.startOf('week'), prevWeek.endOf('week')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.thisMonth,
    getValue: () => {
      const today = dayjs()
      return [today.startOf('month'), today.endOf('day')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.lastMonth,
    getValue: () => {
      const today = dayjs()
      return [today.subtract(1, 'month').startOf('day'), today.endOf('day')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.last3Months,
    getValue: () => {
      const today = dayjs()
      return [today.subtract(3, 'month').startOf('day'), today.endOf('day')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.last6Months,
    getValue: () => {
      const today = dayjs()
      return [today.subtract(6, 'month').startOf('day'), today.endOf('day')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.thisYear,
    getValue: () => {
      const today = dayjs()
      return [today.startOf('year'), today.endOf('day')]
    },
  },
  {
    label: DATE_RANGE_SHORTCUTS.lastYear,
    getValue: () => {
      const today = dayjs()
      // `subtract(1, ...)`, not `subtract(6, ...)`. The preset labelled "Last year" was
      // returning a SIX-year window — a plain typo, but a silent one: it produces a valid
      // range that simply answers a different question, and nothing about the resulting
      // chart says so.
      return [today.subtract(1, 'year').startOf('day'), today.endOf('day')]
    },
  },
]

/**
 * The floor "All time" falls back to when the caller cannot supply a real one.
 *
 * It is a FLOOR, not a claim: Vera has no record of any kind before it, so a range starting
 * here includes everything, and the scan/certificate queries clamp themselves against the
 * data rather than the bound.
 *
 * ⚠️ It is NOT a good input for the ROI hero, and callers on the analytics page should pass
 * a real `dataFloor` (the company's `created_at`) instead. The hero's denominator scales
 * with range LENGTH — `getVeraSpend` charges `ceil(rangeDays / 30)` subscription intervals —
 * so a 2020 floor on a $299/mo plan reports ~$24k of spend and drives the multiple to
 * roughly zero, for a brand that may have been paying for three months. The brand's own
 * signup date is the honest bound; this constant is only the "we were not told" case.
 */
export const VERA_DATA_FLOOR = '2020-01-01'

export interface DateRangeShortcutOptions {
  /**
   * The plan's CURRENT billing period, exactly as the API reports it — the API's
   * `IVeraSpend.current_period_start` / `current_period_end`, or the equivalent fields on
   * the company subscription. Omit it when unknown; the preset is then not offered.
   *
   * Deliberately NOT day-floored downstream. `getVeraSpend` decides the ROI hero is exact
   * with `from >= current_period_start && to <= current_period_end`, so rounding `from`
   * down to midnight would push it BEFORE the period start and put the approximation
   * caveat straight back on the page — the exact thing this preset exists to remove.
   *
   * Known, accepted consequence: a Stripe period boundary is an arbitrary instant, not
   * midnight, so `isWholeDayRange` reports this range as TIMED and the field renders the
   * long `MMM D, YYYY h:mm A` form (and the analytics header widens 280 → 420px for it).
   * That is the honest reading — the billing period genuinely has a clock boundary — and
   * an exact headline figure is worth more than a compact field.
   */
  billingPeriod?: { start: string | Date | Dayjs; end: string | Date | Dayjs } | null
  /**
   * The earliest instant this company can have data — its `created_at`. Falls back to
   * `VERA_DATA_FLOOR`; see that constant for why the fallback is a poor ROI input.
   */
  dataFloor?: string | Date | Dayjs | null
  /**
   * Offer "All time". DEFAULT FALSE — but the reasons for that default have both been
   * dealt with, so read this before assuming it is still forbidden.
   *
   * ── THE TWO ORIGINAL BLOCKERS, AND WHERE THEY STAND ───────────────────────────
   *
   * "All time" is the one preset that can reach back before the company existed, and two
   * things downstream of it produced a NUMBER rather than an error — which is why it was
   * gated rather than merely documented:
   *
   *  1. **The prior-period comparison degenerates.** ✅ FIXED AT THE REQUEST SITE. The KPI
   *     tiles ask the totals endpoint for `compare_to_previous`, and the server's previous
   *     window is `[from − length, from)`, unclamped. From a floor at the company's first
   *     day that window sits entirely before the brand existed, so every tile compares
   *     against a guaranteed zero and `AnalyticsBlock` renders growth-from-nothing as a
   *     green "New" — on every metric at once. `AnalyticsPage` now omits the flag whenever
   *     the previous window is not wholly inside the company's lifetime; see
   *     `previousWindowIsMeasurable` there. Note the artefact was never exclusive to this
   *     preset — any hand-picked range near signup did it too.
   *  2. **The ROI denominator is invented.** ✅ GONE. `getVeraSpend` used to charge
   *     `ceil(rangeDays / 30)` intervals AT TODAY'S PRICE for any range outside the current
   *     billing period, so "all time" multiplied today's plan across the brand's entire
   *     history. Spend is now settled Stripe invoices — real cash, at the price actually
   *     charged — and does not scale with window width at all.
   *
   * So the ROI hero is FIXED to it — not offered among options, which it no longer has
   * (`RoiHeroPeriod`). It stays off by
   * default here because turning it on for a picker is a per-surface product decision about
   * that surface's option list, not a safety question any more.
   *
   * ⚠️ PASS A REAL `dataFloor` WHENEVER YOU ENABLE IT. `VERA_DATA_FLOOR` no longer breaks
   * the denominator, but it still claims a history the brand does not have and pads any
   * bucketed series with years of empty buckets.
   */
  includeAllTime?: boolean
}

/**
 * The shortcut list for a picker that KNOWS something about the company it is filtering.
 *
 * `dateRangeShortcutItems` above is the static list and stays exactly as it is: it is what
 * every table's filter drawer renders, and those pickers have no company context to offer.
 * The two presets added here both need data — the billing period comes from the
 * subscription, "All time" needs a floor — so they cannot live in a module-level constant,
 * which is the whole reason this is a factory rather than three more entries in the array.
 *
 * "This billing period" is FIRST because it is the analytics page's default (see
 * `store/analytics/reducer.ts` for why), and a default that is buried eighth in its own
 * list reads as an oddity rather than as home.
 */
export const makeDateRangeShortcutItems = (
  options: DateRangeShortcutOptions = {}
): PickersShortcutsItem<DateRange<Dayjs>>[] => {
  const { billingPeriod, dataFloor, includeAllTime = false } = options

  const start = billingPeriod?.start ? dayjs(billingPeriod.start) : null
  const end = billingPeriod?.end ? dayjs(billingPeriod.end) : null

  // Offered ONLY when the boundaries are actually known and parse. There is no "approximate
  // with this month" branch on purpose: the point of the preset is that the ROI hero's
  // denominator is EXACT inside it, and a calendar month that merely resembles the billing
  // period would reinstate the approximation while claiming to have removed it.
  const billingPeriodItem: PickersShortcutsItem<DateRange<Dayjs>>[] =
    start?.isValid() && end?.isValid()
      ? [
          {
            label: DATE_RANGE_SHORTCUTS.billingPeriod,
            getValue: (): DateRange<Dayjs> => {
              const today = dayjs().endOf('day')
              // The period END is in the future — it is the next renewal date. The
              // analytics picker caps at `maxDateTime={dayjs().endOf('day')}`, so an
              // unclamped end renders the field in its error state, and a future `to`
              // would in any case be asking for scans that have not happened.
              return [start, end.isBefore(today) ? end : today]
            },
          },
        ]
      : []

  const allTimeItem: PickersShortcutsItem<DateRange<Dayjs>>[] = includeAllTime
    ? [
        {
          label: DATE_RANGE_SHORTCUTS.allTime,
          getValue: (): DateRange<Dayjs> => [
            dayjs(dataFloor ?? VERA_DATA_FLOOR).startOf('day'),
            dayjs().endOf('day'),
          ],
        },
      ]
    : []

  return [...billingPeriodItem, ...dateRangeShortcutItems, ...allTimeItem]
}

/**
 * The label of the preset that EXACTLY produces `value` right now, or `undefined`.
 *
 * ## Why it re-evaluates rather than remembering
 *
 * The obvious implementation is to record which shortcut was clicked. That is the one
 * implementation that can be WRONG: a range hand-edited in the calendar, typed into the
 * field, or promoted from the chart's brush would keep the old label, and a field reading
 * "This month" over a range that is not this month is a false statement about the numbers
 * on the page. No label is a small loss; a stale label is a wrong one, so the label is a
 * pure function of the CURRENT value and is recomputed on every render.
 *
 * ## Exact, to the millisecond
 *
 * `isSame` with no unit, on both ends. Anything looser would let two genuinely different
 * windows share a name — and the presets are already only a day apart in places
 * ("Last month" is a rolling month, "This month" is calendar-to-date), so a day-granular
 * comparison would name the wrong one.
 *
 * ## The one preset that never matches, and why that is correct
 *
 * "Last 24 hours" ends at `dayjs()` — the current instant — so re-evaluating it a second
 * after it was chosen produces a different `to` and the match fails. The field then shows
 * the unnamed range with both clock times, which is the honest reading twice over: the
 * stored window is a fixed 24 hours, not a live one, so calling it "Last 24 hours" an hour
 * later would be untrue — and the times are the only thing that says WHICH 24 hours. Every
 * other preset is day-boundaried and therefore stable for the whole day, so it matches for
 * as long as it remains accurate and stops the moment it does not.
 *
 * `items` should be the SAME array handed to the picker's `shortcuts` slot, so a preset
 * that is not offered can never be named.
 */
export const matchDateRangeShortcut = (
  value: DateRange<Dayjs>,
  items: PickersShortcutsItem<DateRange<Dayjs>>[]
): string | undefined => {
  const [from, to] = value
  if (!from?.isValid() || !to?.isValid()) return undefined

  for (const item of items) {
    // `getValue` is handed an `isValid` predicate by the picker; none of these presets
    // consult it, but the signature requires it and a bare `getValue()` is a type error.
    const [presetFrom, presetTo] = item.getValue({ isValid: () => true })
    if (!presetFrom?.isValid() || !presetTo?.isValid()) continue
    if (from.isSame(presetFrom) && to.isSame(presetTo)) return item.label
  }
  return undefined
}

/**
 * `day` slot. NOTE: the visible range/selected/today colours are applied via `dateRangeDaySx`
 * (class selectors) — this MUI version doesn't match these `ownerState` predicates, so the root
 * variants stay minimal to avoid a stray root-level tint.
 */
export const DateRangePickerDay = styled(MuiDateRangePickerDay)(() => ({
  variants: [
    {
      props: ({ today }) => today,
      style: { borderRadius: 45 },
    },
  ],
})) as React.ComponentType<DateRangePickerDayProps<Dayjs>>

const CalendarHeaderRoot = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 16px',
  alignItems: 'center',
})

/** `calendarHeader` slot — month label with chevron nav on the outer calendars only. */
export function DateRangeCalendarHeader(props: PickersRangeCalendarHeaderProps<Dayjs>) {
  const { currentMonth, onMonthChange, month, calendars, monthIndex } = props

  const selectNextMonth = () => onMonthChange(currentMonth.add(calendars, 'month'), 'left')
  const selectPreviousMonth = () => onMonthChange(currentMonth.subtract(calendars, 'month'), 'right')

  return (
    <CalendarHeaderRoot>
      <IconButton
        onClick={selectPreviousMonth}
        sx={[monthIndex === 0 ? { visibility: null } : { visibility: 'hidden' }]}
        title={`Previous ${calendars} month${calendars === 1 ? '' : 's'}`}
      >
        <ChevronLeftIcon strokeWidth={1} />
      </IconButton>
      <Typography>{month.format('MMMM YYYY')}</Typography>
      <IconButton
        onClick={selectNextMonth}
        sx={[monthIndex === calendars - 1 ? { visibility: null } : { visibility: 'hidden' }]}
        title={`Next ${calendars} month${calendars === 1 ? '' : 's'}`}
      >
        <ChevronRightIcon strokeWidth={1} />
      </IconButton>
    </CalendarHeaderRoot>
  )
}

/**
 * ── THE PICKER'S SELECTION PURPLE ─────────────────────────────────────────────────
 *
 * ONE source for the three surfaces that state "selected" inside this picker — the day
 * circle, the hour/minute/AM-PM item, and the `today` ring — so the calendar half and the
 * clock half can never drift apart again. They were three literal `brand[800]`s before, and
 * the clock's comment recorded "same treatment the selected DAY gets" in prose rather than
 * in code, which is exactly the drift `fieldChrome.ts` and `navChrome.ts` exist to prevent.
 *
 * ## Why `brand[600]` and not the sidebar's own purple
 *
 * The ask was "the same light purple we use on sidebar nav", and the BAND in `dateRangeDaySx`
 * takes that literally — `navSelected()`, the shared function, not a restatement of its
 * alphas. The FILL cannot: the sidebar's purple is a TINT (`alpha(brand[500], 0.12)`) behind
 * dark text, and there is no solid-fill member of that treatment to copy. The selected day is
 * a filled circle carrying WHITE text, so the token is constrained by contrast, measured in
 * the browser against `#FFFFFF`:
 *
 *   brand[800]  #5D2DAA   8.67:1   ← was. Passes easily, and reads as the harsh block.
 *   brand[700]  #6F36CB   6.85:1   ← hover.
 *   brand[600]  #7F48DF   5.39:1   ← now. The LIGHTEST step that still clears AA's 4.5:1.
 *   brand[500]  #8F68EA   3.94:1   ← the sidebar's own token. FAILS AA for 14px day numbers.
 *
 * So `brand[500]` is not available for a white-on-fill surface, and dropping the text to a
 * dark colour to reach for it would trade a stated contrast requirement for a hue preference.
 * `brand[600]` is two steps lighter than what shipped, on the same ramp, and is already the
 * house ACCENT — it is the colour this very field's border takes on focus (`fieldAccentColor`),
 * so the open picker and the field it dropped out of now agree.
 *
 * The ramp is identical in both themes (see `brandText.ts`), so these figures hold in dark
 * too; the dark paper only changes the BAND's contrast, measured in `dateRangeDaySx`.
 *
 * Hover deepens one step to `brand[700]`, the same direction `brandText.ts` takes.
 */
const pickerSelectedFill = (theme: Theme) => theme.palette.brand[600]
const pickerSelectedHoverFill = (theme: Theme) => theme.palette.brand[700]

/**
 * The two endpoint classes, named once so the combined-endpoint rule in `dateRangeDaySx` is
 * built FROM the single-endpoint selectors rather than retyped alongside them — a typo in a
 * hand-copied third selector would fail silently, which is the failure mode that block fixes.
 */
const RANGE_START_CLASS = '.MuiDateRangePickerDay-rangeIntervalDayHighlightStart'
const RANGE_END_CLASS = '.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd'

/**
 * `navSelected` behind the duplicate-`@mui/material` cast.
 *
 * Calling a `@repo/ui` theme helper from this module is a `TS2345` — see the long note on
 * `dateRangeFieldChrome` below: pnpm has resolved TWO copies of `@mui/material` (one under
 * `react@18.3.1`, one under `react@19.1.0`), so the `Theme` this module imports and the one
 * those helpers are typed against are structurally identical but nominally different, and the
 * error count scales with CALL SITES. Measured: adding a bare `navSelected(theme)` took the
 * platform baseline from 1035 to 1036.
 *
 * The cast is nominal only — same package, same version, two resolved copies — and it is
 * `Parameters<typeof navSelected>[0]` rather than `any` so it stays bound to the helper's real
 * signature and would break if that signature ever changed. It is deliberately NOT applied to
 * the three pre-existing errors in `dateRangeFieldChrome`: they are the platform's recorded
 * baseline and belong to whoever fixes the duplicate dependency, not to this change.
 */
const rangeBandSurface = (theme: Theme) =>
  navSelected(theme as Parameters<typeof navSelected>[0])

/**
 * The hour / minute / AM-PM columns of a DATE-TIME range picker.
 *
 * ── WHY THIS IS FOLDED INTO THE PAPER'S sx ────────────────────────────────────────
 *
 * There is no `slotProps` entry for the digital clock, and the two things that DO reach it
 * — `day` (one cell) and `layout` (owned by each call site, because it carries the
 * action-bar padding) — are the wrong shape. `desktopPaper` is the only slot in the shared
 * chrome that is an ANCESTOR of the clock, so the rules ride there as descendants.
 *
 * That also makes it free for the other consumer: `DateRangePickerFilter` renders a
 * `DateRangePicker`, which has no clock at all, so none of these selectors match anything
 * in the filter drawer and its rendered output is byte-identical.
 *
 * Class selectors, not `styled` variants — the same reason `dateRangeDaySx` uses them: this
 * MUI version does not match the `ownerState` predicates. The real classes were read off
 * the live DOM rather than guessed; the item carries
 * `MuiMultiSectionDigitalClockSection-item` alongside `MuiMenuItem-root`, and selection is
 * the standard `Mui-selected`.
 *
 * ⚠️ Specificity is NOT uniform across these rules, so `!important` is not decoration on
 * either of them. Riding the paper adds one class, so the SELECTED-ITEM rule lands at
 * (0,3,0) and beats MUI's (0,2,0) on weight — but the MAX-HEIGHT rule lands at (0,2,0) and
 * merely TIES the compound selector MUI emits for the same property, which then wins on
 * source order. That tie is what made the first attempt fail silently; see the max-height
 * block below.
 */
export const dateRangeTimeColumnSx = (theme: Theme) => ({
  /**
   * ── THE BOTTOM CUT-OFF ──────────────────────────────────────────────────────────
   *
   * MUI caps the clock at `max-height: 360px`, and the rows are 44px tall on a 46px pitch
   * (measured off the live DOM: item `offsetTop`s run 4, 50, 96, 142, …, so pitch is 46 and
   * the first row starts 4px down). 360 is not a whole number of rows: seven fit (the 7th
   * ends at 324) and the 8th spans 326–370, so 34 of its 44px show and it is sliced through
   * the middle of the text. That slice is what reads as "cut off".
   *
   * 372 = 4 + 8 × 46 — the box now ends exactly where the NINTH row would begin, so eight
   * rows are whole and nothing is part-drawn. Measured before/after on the live popup:
   *
   *   max-height  full rows  next row showing  clock bottom → divider  popup height
   *   360 (MUI)       7           34px               15.3px               468.5px
   *   372             8            0px                7.3px               468.5px
   *   374             8            2px                6.0px               468.5px
   *
   * The popup does NOT grow, and that is not luck: the layout's height is set by the
   * shortcuts column (384px), which is taller than anything 372 can push. Any value above
   * 384 WOULD resize the popup, which is why the fit was solved downward to the nearest
   * whole row instead of upward to the divider — 374 reaches 1.3px closer at the cost of a
   * 2px sliver of the ninth row, and a sliver is the defect being fixed.
   *
   * The cap has to be lifted on BOTH elements: the root and the section carry it
   * independently, and the section is the one that actually scrolls.
   *
   * `!important` IS load-bearing here, and this was measured rather than assumed. MUI emits
   * the 360px cap from the DateRangeCalendar's own class as a COMPOUND selector —
   * `.css-…-MuiDateRangeCalendar-root.MuiMultiSectionDigitalClock-root` and
   * `.css-…-MuiDateRangeCalendar-root .MuiMultiSectionDigitalClockSection-root` — which is
   * specificity (0,2,0), exactly TYING the (0,2,0) these rules get as descendants of the
   * paper's own class. A tie is broken by source order, and emotion emits MUI's later, so
   * the plain declarations lost silently: the rules were in the stylesheet, at 372px, and
   * the computed value stayed 360. (Same reason `dateRangeDaySx` below carries `!important`
   * on the selected-day background.)
   */
  '& .MuiMultiSectionDigitalClock-root': { maxHeight: '372px !important' },
  '& .MuiMultiSectionDigitalClockSection-root': { maxHeight: '372px !important' },

  /**
   * ── THE SELECTED VALUE ──────────────────────────────────────────────────────────
   *
   * Same treatment the selected DAY gets in `dateRangeDaySx` — and now literally the same
   * value, read from `pickerSelectedFill` rather than restated, because "same as the day"
   * was a COMMENT while the two were independent `brand[800]` literals. Before any of this
   * the hour/minute/AM-PM selection was MUI's default `primary` grey (`rgb(161,161,170)`
   * with near-black text), so a picker showing one range stated its two halves in two
   * different visual languages.
   */
  '& .MuiMultiSectionDigitalClockSection-item.Mui-selected': {
    backgroundColor: `${pickerSelectedFill(theme)} !important`,
    color: '#FFFFFF',
    '&:hover, &:focus, &:focus-visible': {
      backgroundColor: `${pickerSelectedHoverFill(theme)} !important`,
    },
  },
})

/**
 * `desktopPaper` slot sx — the popup card the calendar drops into.
 *
 * Also carries the time-column rules; see `dateRangeTimeColumnSx` for why they cannot live
 * in a slot of their own, and why they are inert for the filter-drawer consumer.
 */
export const dateRangeDesktopPaperSx = (theme: Theme) => ({
  ...dateRangeTimeColumnSx(theme),
  mt: 2,
  // The radius of the field this drops out of, not a number of its own — see
  // `FILTER_DROPDOWN_RADIUS`. It was 12px against an 8px input.
  borderRadius: FILTER_DROPDOWN_RADIUS,
  // Flat, uniform dark surface (kill MUI's dark-mode elevation overlay so the calendar reads
  // as one background, not a lighter floating card).
  backgroundImage: 'none',
  backgroundColor: 'background.paper',
  // ...which leaves it with no edge against a surface of the same colour. Same `MuiMenu`
  // border value the other filter dropdowns carry.
  border: `1px solid ${theme.palette.divider}`,
})

/**
 * `day` slot sx — the brand-purple range.
 *
 * Targets the real MUI classes: the styled-component `variants` predicates above don't match
 * this version's `ownerState`, so class selectors are the reliable path.
 */
export const dateRangeDaySx = (theme: Theme) => ({
  /**
   * In-range band — `navSelected()`, the SIDEBAR's own selected-row surface, called rather
   * than restated so the two can never drift.
   *
   * It replaces `alpha(brand[800], 0.18)`, and the change is not only "lighter". That value
   * was mode-BLIND: `brand[800]` (#5D2DAA) is a dark purple, so in dark mode an 18% wash of
   * it over a near-black paper was a dark tint on a dark surface. Measured on the live popup,
   * band against its own paper:
   *
   *                                     light (paper #FFF)   dark (paper #0F0F12)
   *   alpha(brand[800], 0.18)  (was)         1.361:1               1.088:1
   *   navSelected()            (now)         1.149:1               1.254:1
   *
   * So the trade is not "everything got fainter" — light steps DOWN, which is the ask, and
   * dark steps UP, fixing a band that was barely delineating the range at all. `navSelected`
   * is mode-aware by construction (`alpha(brand[500], 0.12)` light, `0.20` dark), so one call
   * does both.
   *
   * The AMOUNT is deliberately the sidebar's, not a bespoke one. The band's area per cell
   * (~40x36) is close to a collapsed sidebar row's chip, which is the geometry those alphas
   * were tuned against; `navSelectedSubtle`'s lower figures exist for the much larger
   * settings pill and would be under-visible here.
   */
  '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
    backgroundColor: rangeBandSurface(theme),
  },
  /**
   * ── THE ENDPOINT CORNERS ────────────────────────────────────────────────────────
   *
   * Round the OUTER corner of each endpoint and keep the INNER corners square, so the band
   * connects flush to the selected circle instead of leaving a notch.
   *
   * Each rule states all FOUR corners in one shorthand rather than a `borderRadius: 0` reset
   * followed by two longhands, and that is the fix, not a tidy-up. The reset is load-bearing
   * — without it a `today` endpoint keeps the 45px round MUI puts on the day root
   * (`.MuiDateRangePickerDay-root { border-radius: 45px }`) on its inner side and opens a gap
   * — but as a SEPARATE declaration it also reset the corners the OTHER rule had just set.
   *
   * When one day is both start and end — a single-day range, which is exactly what the
   * default `This billing period` resolves to on any day of a billing cycle — both classes
   * match the same element at equal (0,2,0) specificity, so the tie broke on source order:
   * `End`'s reset landed last and wiped `Start`'s two left corners. The day rendered square
   * on the left and round on the right. MUI's own base rules have the same shape and the
   * same latent bug, which is why fixing this by deleting our rules would not have worked.
   *
   * A shorthand cannot half-apply, so order no longer decides anything between these two.
   */
  [`&${RANGE_START_CLASS}`]: {
    borderRadius: '50% 0 0 50%',
  },
  [`&${RANGE_END_CLASS}`]: {
    borderRadius: '0 50% 50% 0',
  },
  /**
   * ...and the both-ends case gets its own rule at (0,3,0), which beats BOTH of the above and
   * both of MUI's, so it cannot lose a tie to anything. Belt and braces on purpose: the
   * shorthands above already make the outcome order-independent, but this states the
   * single-day intent explicitly — a fully round capsule — instead of leaving it as whichever
   * of two half-rules happens to win.
   */
  [`&${RANGE_START_CLASS}${RANGE_END_CLASS}`]: {
    borderRadius: '50%',
  },
  // Selected endpoints: filled brand[600] circle, white text. See `pickerSelectedFill` for
  // why brand[600] and not the sidebar's own brand[500] (contrast, measured).
  '& .MuiPickersDay-root.Mui-selected': {
    backgroundColor: `${pickerSelectedFill(theme)} !important`,
    color: '#FFFFFF',
    '&:hover, &:focus': { backgroundColor: `${pickerSelectedHoverFill(theme)} !important` },
  },
  // Today ring in the same accent (when not selected).
  '& .MuiPickersDay-today:not(.Mui-selected)': {
    borderColor: pickerSelectedFill(theme),
  },
})

/** `shortcuts` slot sx — the preset list down the left of the popup. */
export const dateRangeShortcutsSx = (theme: Theme) => ({
  minWidth: '180px',
  pr: 3,
  py: 0,
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  li: {
    p: 0,
    borderRadius: 3,
    minHeight: '48px',
    '& .MuiButtonBase-root': {
      p: 2,
      background: 'transparent',
      fontSize: 16,
      minWidth: '100%',
      minHeight: '48px',
      justifyContent: 'flex-start',
      boxShadow: 'none',
    },
    '&:hover': {
      background: theme.palette.grey[200],
    },
  },
})

/**
 * ── THE LEADING-ICON GEOMETRY ──────────────────────────────────────────────────────
 *
 * Three numbers, and they are related rather than independently chosen:
 *
 *   12  the icon's left inset. It is the input's OWN horizontal padding (`px: 3` on the
 *       4px base), so the glyph starts exactly where the text used to.
 *   18  the glyph's box, matching every other 49px field's leading affordance.
 *   40  where the text now starts: 12 + 18 + a 10px gap.
 *
 * The gap is 10 rather than 12 because the lucide calendar's drawn artwork stops ~1px
 * inside its 18px box on each side, so an even 12 measures optically wide.
 */
const DATE_FIELD_ICON_INSET = 12
export const DATE_FIELD_ICON_SIZE = 18
const DATE_FIELD_TEXT_INSET = 40
/** 40 − 12 − 18. Named because the labelled variant applies it as a real flex `gap`. */
export const DATE_FIELD_GAP = DATE_FIELD_TEXT_INSET - DATE_FIELD_ICON_INSET - DATE_FIELD_ICON_SIZE

/*
 * `DATE_RANGE_FIELD_WIDTH = 280` and `DATE_TIME_RANGE_FIELD_WIDTH = 420` used to live here,
 * as "the two widths the analytics date field has always had". They are DELETED rather than
 * kept for reference, because they had become false and were still being derived from.
 *
 * They described the field as a FIXED-width box. It has not been one since the labelled
 * variant moved the chrome to the root and made it `width: fit-content` — the field is now
 * the sum of its parts (421px on the running page, not 280), and its width moves with the
 * preset label. The only thing still reading them was `dateRangeTextWidth`, which subtracted
 * the insets to recover a date box — reconstructing a number from a total that no longer
 * described anything, and inheriting ~19px of error at the widest value. See below.
 */

/**
 * The width of the DATES alone — the FALLBACK box the `input` gets in a browser that cannot
 * size it to its own content. See `dateRangeFieldWithIconSx` for the content-sizing path.
 *
 * ## These are the WIDEST the format can render, measured — not derived from the field width
 *
 * They used to be `DATE_RANGE_FIELD_WIDTH − insets`, i.e. 226 / 366, and that arithmetic
 * described a field that no longer exists. 280 and 420 were the widths of a FIXED-width box
 * with no leading affordance; the labelled variant made the root `width: fit-content`, so
 * the field is whatever its contents come to (421px today) and the 280 anchor stopped
 * meaning anything. What it left behind was ~19px of dead space inside the date box at the
 * WIDEST possible value, and ~51px at a typical one — measured on the running page as a
 * 63.9px gap from the last date glyph to the field's right border, against a 13px left
 * inset. That imbalance is what "too much right padding" was describing.
 *
 * So the numbers below are now the only thing they ever needed to be: enough to render the
 * longest value the format can produce, and no more. Measured in the browser at the field's
 * real font (`16px Lato`), exhaustively over every month × day × year × time combination:
 *
 *   whole-day  "May 10, 2026 – May 10, 2026"                    207.1px  →  212
 *   timed      "May 10, 2026 10:00 AM – May 10, 2026 10:00 AM"  353.0px  →  358
 *
 * The ~5px of headroom is for font-fallback drift (the measurement is Lato; a cold cache
 * renders the stack's fallback for a frame), because in the fallback path clipping the date
 * is a far worse failure than a few px of slack.
 */
export const DATE_RANGE_TEXT_WIDTH = 212
export const DATE_TIME_RANGE_TEXT_WIDTH = 358

export const dateRangeTextWidth = (wholeDay: boolean): number =>
  wholeDay ? DATE_RANGE_TEXT_WIDTH : DATE_TIME_RANGE_TEXT_WIDTH

/**
 * The three `fieldChrome` values this picker paints, resolved in ONE place.
 *
 * ## Why the variants share a resolver instead of each calling `@repo/ui` directly
 *
 * The two variants below paint the same border, fill and hover accent on different
 * elements, so the obvious shape is for each to call `fieldBorderColor` / `fieldFill` /
 * `fieldAccentColor` itself. That is what the second variant did first, and it cost THREE
 * NEW TYPE ERRORS for no behavioural difference.
 *
 * The cause is pre-existing and repo-wide, not local: `pnpm` has resolved TWO copies of
 * `@mui/material` (one under `react@18.3.1`, one under `react@19.1.0`), so the `Theme` this
 * module imports and the `Theme` those `@repo/ui` helpers are typed against are structurally
 * identical but nominally different — every call is a `TS2345`, and the count therefore
 * scales with the number of CALL SITES. Six calls, six errors; three calls, three.
 *
 * Consolidating here keeps the platform's error baseline flat while the underlying duplicate
 * dependency is someone else's problem to fix. It is also the better factoring on its own
 * merits: the fill and the border are one decision, and a second variant that resolved them
 * independently could drift from the first.
 */
const dateRangeFieldChrome = (theme: Theme) => {
  const accent = fieldAccentColor(theme)
  return {
    border: `1px solid ${fieldBorderColor(theme)}`,
    backgroundColor: fieldFill(theme),
    /** §31: hover changes the BORDER, never the fill. */
    hoverBorder: `1px solid ${accent}`,
    hoverBorderColor: accent,
  }
}

/**
 * The inner `input`'s chrome for the PLAIN variant — the one every table's filter drawer
 * renders.
 *
 * Both sides of the horizontal padding are named rather than written as the `px: 3`
 * shorthand: `12px` is exactly what `px: 3` computed to, so the drawer's field is unchanged,
 * and an explicit pair cannot be half-overridden by a spread that adds only one side.
 *
 * The icon variant no longer shares this — it moves the chrome to the root; see
 * `dateRangeFieldWithIconSx` for why.
 */
const dateRangeInputSx = (theme: Theme) => {
  const chrome = dateRangeFieldChrome(theme)
  return {
    border: chrome.border,
    // 49px, matching every other field.
    height: 49,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: `${FIELD_RADIUS}px`,
    fontSize: 16,
    paddingLeft: `${DATE_FIELD_ICON_INSET}px`,
    paddingRight: `${DATE_FIELD_ICON_INSET}px`,
    backgroundColor: chrome.backgroundColor,
    cursor: 'pointer',
    ':hover': {
      border: chrome.hoverBorder,
    },
  }
}

/**
 * `textField` slot sx — the field the picker drops out of, at the 49px `CONTROL_HEIGHT` band
 * (§29/§31) with `fieldChrome` fill and border, so it is the same object as the text fields
 * beside it.
 *
 * The visible field here is the INNER `input`: the fieldset border is removed and the
 * fill/border/height are painted on `input`, unlike every other field, which paints the root.
 * So the root's default horizontal padding would inset the box the user actually sees — hence
 * the `MuiInputBase-root { padding: 0 }` reset.
 *
 * §31: hover and focus change the BORDER to brand, never the fill.
 */
export const dateRangeFieldSx = (theme: Theme) => ({
  width: '100%',
  '& fieldset': {
    border: `none !important`,
  },
  '& .MuiInputBase-root': { padding: 0 },
  input: dateRangeInputSx(theme),
})

/**
 * The same field WITH a leading affordance in the `startAdornment` — a calendar glyph, and
 * optionally a preset NAME beside it ("This month · Aug 1 – Aug 28").
 *
 * ## WHY THE CHROME MOVES TO THE ROOT HERE, AND ONLY HERE
 *
 * `dateRangeFieldSx` above paints the border, fill, height and radius on the inner `input`
 * rather than on the root — unusual for this app, and the reason a `startAdornment` does
 * not work on it. The root is then a bare `padding: 0` flex container, so a
 * `position="start"` adornment is a flex SIBLING rendered at x=0, i.e. OUTSIDE the border
 * the user perceives as the field, pushing the bordered box to the right. That is the
 * `TODO: DATE ICON ADORNMENT DOESNT WORK` recorded in `DateRangePickerFilter`: not a MUI
 * quirk, a direct consequence of where the chrome is painted.
 *
 * An earlier version of this variant worked around it by taking the adornment OUT OF FLOW
 * (`position: absolute` over the input, with the input's `padding-left` widened to clear
 * it). That is fine for a fixed-width glyph and breaks for a variable-width one: an
 * absolutely-positioned box contributes nothing to layout, so nothing can size around it,
 * and a preset name would need its rendered width measured and fed back as a `padding-left`
 * on every label change. Measuring in order to lay out is the thing flexbox exists to avoid,
 * and the measurement would have to survive a `ResizeObserver` that fires zero times in the
 * Claude preview pane.
 *
 * So this variant does the principled repair instead — border, fill, height, radius and
 * horizontal padding on the ROOT; the `input` reduced to transparent text — which is how
 * every other field in the app is built and which makes the adornment an ordinary in-flow
 * flex item. It is still ADDITIVE: `dateRangeFieldSx` is unchanged, so every table's filter
 * drawer renders exactly as before (§36). Only this call site opts in.
 *
 * ## THE GEOMETRY IS EXACT, NOT APPROXIMATE
 *
 *   12 (root padding-left) + 18 (glyph) + 10 (gap) + [label + 10] + textWidth + 12 (right)
 *
 * The root is `width: fit-content`, so the field is the sum of those parts and nothing else
 * — a label adds precisely its own rendered width plus one gap. Measured on the running
 * page with a label present: 421px total, glyph at 13px from the left border.
 *
 * ⚠️ An earlier version of this comment claimed the sum came to "precisely the 280 / 420 the
 * field has always been". That was true of the FIXED-width predecessor and false of this
 * variant, and the stale claim is what kept `dateRangeTextWidth` deriving the date box by
 * subtracting insets from 280 — carrying ~19px of dead space at the widest value and ~51px
 * at a typical one. See that export for the measured replacement.
 *
 * The DATES no longer sit in a fixed box: the `input` sizes to its own text where
 * `field-sizing` is supported, so the trailing gap equals the 13px leading inset instead of
 * absorbing the whole difference. The worst-case box remains as the fallback.
 *
 * `pointerEvents: 'none'` on the adornment so the leading block is not a click target of its
 * own: the open affordance is an `onClick` on the TextField root, and with pointer events
 * off the click also lands on the input and focuses it, which is what clicking a text field
 * should do.
 *
 * §31: hover changes the BORDER, never the fill — same rule, now applied to the root because
 * that is where the border now is.
 */
export const dateRangeFieldWithIconSx = (theme: Theme, textWidth: number) => {
  const chrome = dateRangeFieldChrome(theme)
  return {
    width: 'fit-content',
    '& fieldset': {
      border: `none !important`,
    },
    '& .MuiInputBase-root': {
      padding: `0 ${DATE_FIELD_ICON_INSET}px`,
      gap: `${DATE_FIELD_GAP}px`,
      border: chrome.border,
      // 49px, matching every other field.
      height: 49,
      boxSizing: 'border-box',
      borderRadius: `${FIELD_RADIUS}px`,
      backgroundColor: chrome.backgroundColor,
      cursor: 'pointer',
      ':hover': {
        borderColor: chrome.hoverBorderColor,
      },
    },
    '& .MuiInputAdornment-root': {
      // MUI gives a `position="start"` adornment `margin-right: 8px` and a `max-height` tuned
      // to its own idea of the field. The gap above owns the spacing now, and a second source
      // of it would put the glyph 8px off the geometry stated in this comment.
      margin: 0,
      height: 'auto',
      maxHeight: 'none',
      // Never squeezed by the input beside it — the adornment IS the label, so shrinking it
      // would truncate the preset name rather than the dates.
      flexShrink: 0,
      pointerEvents: 'none',
      color: theme.palette.text.secondary,
    },
    input: {
      /**
       * ── THE DATE BOX SIZES TO ITS OWN TEXT, WITH A MEASURED FALLBACK ────────────────
       *
       * `width: ${textWidth}px` is the FALLBACK, not the intent. It is the widest the format
       * can render (see `dateRangeTextWidth`), so the dates can never clip — but a fixed box
       * means every value narrower than the worst case pays the difference as dead space on
       * the RIGHT, since the text is left-aligned. Measured on the running page with the
       * default range: text 175.1px in a 226px box, i.e. 50.9px of slack, giving a 63.9px
       * gap from the last glyph to the field's right border against a 13px left inset. That
       * is what was reported as too much right padding, and re-deriving the constant only
       * removes ~19px of it — the rest is structural to a fixed box.
       *
       * `field-sizing: content` removes it exactly: the input takes the width of its own
       * value, so the trailing gap becomes the root's 12px padding + 1px border = 13px, the
       * same number as the leading inset. Verified in the browser before writing this —
       * probed live on the real field, it resolved to 175.06px against a canvas
       * `measureText` of 175.06px. No JS, no measurement code, and (importantly here) no
       * `ResizeObserver`, which fires ZERO times in the Claude preview pane.
       *
       * WHY `@supports` AND NOT JUST THE PROPERTY. `field-sizing` is Chromium 123+ and
       * Safari 26+; Firefox does not ship it. Written unguarded, `width: auto` would apply
       * everywhere and only SOME engines would size to content — the rest fall back to an
       * `<input>`'s intrinsic width, which is the `size` attribute's ~20 characters and is
       * measured at 170px here, i.e. it CLIPS a full date pair. The guard makes the
       * degradation the safe direction: no support, no `width: auto`, and the worst-case box
       * applies. Both paths were measured; neither clips.
       *
       * ⚠️ This is the LABELLED variant only. `dateRangeFieldSx` — what every table's filter
       * drawer renders — is untouched, so the drawer's field keeps its `dateRangeInputSx`
       * chrome byte for byte (§36: shared chrome, per-call-site behaviour).
       */
      width: `${textWidth}px`,
      flexShrink: 0,
      minWidth: 0,
      // The chrome is on the root now; the input is just text on it.
      border: 'none',
      backgroundColor: 'transparent',
      height: '100%',
      padding: 0,
      fontSize: 16,
      cursor: 'pointer',
      /**
       * LAST in the object, and that is not cosmetic. `@supports` adds no specificity, so it
       * wins on SOURCE ORDER alone — and emotion serialises this object in key order. Placed
       * above, the `minWidth` below it would override the one in here for every browser that
       * does support the property, which is the silent half of the failure.
       */
      '@supports (field-sizing: content)': {
        fieldSizing: 'content',
        width: 'auto',
        /**
         * The floor for an EMPTY field. With `field-sizing: content` an input with no value
         * sizes to its placeholder, and this picker's is the format mask — but a mask is not
         * guaranteed, and a field that collapsed to nothing would leave no click target (the
         * root IS the open affordance). One date's worth of width, never less.
         */
        minWidth: `${Math.round(textWidth / 2)}px`,
      },
    },
  }
}
